import React from "react";
import { withSpan } from "../../../Apm";
import PropTypes from "prop-types";
import { FontIconButton } from "orion-components/CBComponents";
import { TargetingIcon, getIcon, getIconByTemplate } from "orion-components/SharedComponents";
import { IconButton, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import { MoreHoriz } from "@mui/icons-material";
import { HomeCity } from "mdi-material-ui";
import getButtons from "./getButtons";
import getAction from "./getAction";
import toUpper from "lodash/toUpper";
import { Translate, getTranslation } from "orion-components/i18n";
import { useDispatch, useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { getSelectedContextData } from "orion-components/Profiles/Selectors";
import { loadProfile } from "orion-components/ContextPanel/Actions";

const propTypes = {
	contextId: PropTypes.object.isRequired,
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	name: PropTypes.string,
	displayType: PropTypes.string,
	type: PropTypes.string,
	secondaryText: PropTypes.string,
	description: PropTypes.string,
	geometry: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
	actions: PropTypes.array,
	scrolledUp: PropTypes.bool,
	handleExpand: PropTypes.func,
	appId: PropTypes.string,
	isHiding: PropTypes.bool,
	profileIconTemplate: PropTypes.string,
	readOnly: PropTypes.bool,
	selectFloor: PropTypes.func,
	zetronPhoneVisible: PropTypes.bool,
	facilityPlacement: PropTypes.object
};

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
const appsWithFacilityProfile = ["facilities-app", "map-app", "events-app"];

const defaultProps = {
	name: "",
	displayType: "",
	type: "",
	description: "",
	geometry: null,
	actions: null,
	scrolledUp: false,
	handleExpand: null,
	isHiding: false,
	profileIconTemplate: null,
	dir: "ltr",
	selectFloor: () => { },
	facilityPlacement: null
};

const SummaryWidget = ({
	contextId,
	id,
	name,
	displayType,
	type,
	secondaryText,
	description,
	geometry,
	actions,
	scrolledUp,
	handleExpand,
	profileIconTemplate,
	readOnly,
	selectFloor,
	zetronPhoneVisible,
	types,
	typeId,
	facilityPlacement
}) => {
	const dispatch = useDispatch();
	const appId = useSelector((state) => state?.appId || state?.application?.appId);
	const entity = useSelector((state) => getSelectedContextData(state)(contextId, "entity"));
	const ownerName = entity?.ownerName;
	const feedId = entity?.feedId;
	const user = useSelector((state) => state.session.user.profile);
	if (zetronPhoneVisible) user["zetronSystemAvailable"] = true;
	const buttons = entity && getButtons(user, contextId, appId, actions, readOnly);
	const dir = useSelector((state) => getDir(state));
	const customActions = [...(entity?.actions ?? []), ...(entity?.entityData?.properties?.actions ?? [])];

	if (buttons) {
		buttons.map((button) => {
			actions.forEach((action) => {
				if (action.name === button.name) {
					button.action = action.action;
					if (hasOwn(action, "disabled")) {
						button.disabled = action.disabled;
					}
				}

				// Prevents multiple simultaneous clicks
				// On-click, set flag in parent profile, pass down as action.debounce
				if (action.debounce) {
					button.disabled = true;
				}
			});
			if (!button.action) {
				button.viewable = false;
			}
			return button;
		});
	}

	const localizeType = (type) => {
		const isRectangleShape = entity?.entityData?.properties?.isRectangle;
		const isCircle = entity?.entityData?.properties?.isCircle;
		if (isRectangleShape) {
			return getTranslation("global.sharedComponents.shapeEditor.components.shapeSelector.rectangle");
		} else if (isCircle) {
			return getTranslation("global.sharedComponents.shapeEditor.components.shapeSelector.circle");
		}
		switch (type) {
			case "Facility":
			case "Track":
			case "Line":
			case "Polygon":
			case "Camera":
			case "Template":
			case "Planned":
			case "Emergent":
				return getTranslation(`global.profiles.widgets.summary.${type}`);
			default:
				return type;
		}
	};

	const generateSecondaryText = (
		<>
			<div>
				{ownerName
					? getTranslation(
						"global.profiles.widgets.summary.createdBy",
						"",
						localizeType(displayType || type),
						ownerName
					)
					: localizeType(displayType || type)
				}
			</div>
			{secondaryText && <div>{secondaryText}</div>}
		</>
	);

	const openFacilityProfile = event => {
		event.preventDefault();

		const facilityId = facilityPlacement?.facility?.id;
		const facilityName = facilityPlacement?.facility?.name;
		if (!facilityId) {
			return;
		}

		if (appsWithFacilityProfile.includes(appId)) {
			dispatch(loadProfile(facilityId, facilityName, "facility", "profile"));
			return;
		}

		window.open(`/facilities-app/#/entity/${facilityId}/`, "_blank");
	};

	return (
		<div className={`summary-wrapper ${scrolledUp ? "scrolled-up" : ""} `} data-testid="profile-summary">
			<ListItem className="summary-info" disableGutters data-testid="profile-summary-info">
				{geometry && (
					<TargetingIcon id={id} feedId={feedId} geometry={geometry || null} selectFloor={selectFloor} />
				)}
				{type && getIconByTemplate(type, entity, "2rem", profileIconTemplate, types, typeId) && (
					<ListItemIcon
						style={{
							color: "#828283",
							fontSize: "2rem",
							minWidth: 0,
							marginLeft: 8,
							marginRight: 8
						}}
					>
						{getIconByTemplate(type, entity, "2rem", profileIconTemplate, types, typeId)}
					</ListItemIcon>
				)}
				<ListItemText
					className={` ${scrolledUp ? "MuiTypography-noWrap" : ""}`}
					primary={toUpper(name || id)}
					secondary={generateSecondaryText}
					primaryTypographyProps={{
						style: {
							color: "#FFF",
							lineHeight: 1,
							textOverflow: "ellipsis",
							overflow: "hidden",
							wordWrap: "break-word",
							direction: dir
						},
						variant: "h6"
					}}
					secondaryTypographyProps={{
						style: { color: "#828283", direction: dir },
						noWrap: scrolledUp
					}}
					style={{ marginRight: 8, marginLeft: 8 }}
				/>
				{scrolledUp && (buttons || description) && (
					<IconButton onClick={handleExpand} data-testid="profile-summary-expand-button">
						<MoreHoriz style={{ color: "#FFF" }} />
					</IconButton>
				)}
			</ListItem>

			{!scrolledUp && (buttons || description || facilityPlacement) && (
				<div style={{ padding: "0 10px" }}>
					{description && (
						<div
							className="summary-description"
							style={dir === "rtl" ? { textAlign: "right", marginRight: "5px" } : {}}
							data-testid="profile-summary-description"
						>
							<p>{description}</p>
						</div>
					)}
					{facilityPlacement && (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: 6,
								color: "#FFF",
								fontSize: 14,
								margin: "0 0 8px 5px"
							}}
							data-testid="profile-summary-facility-placement"
						>
							<HomeCity style={{ fontSize: 20, color: "#B5B9BE" }} />
							<span>
								<Translate value="global.profiles.widgets.summary.locatedOn" />{" "}
								<a
									className="cb-font-link"
									href={`/facilities-app/#/entity/${facilityPlacement.facility?.id}/`}
									onClick={openFacilityProfile} data-testid="orion-components-profiles-widgets-summary-summary-widget-a"
								>
									{facilityPlacement.facility?.name}: {facilityPlacement.floorPlan?.name}
								</a>
							</span>
						</div>
					)}
					<div
						style={{
							display: "flex",
							alignItems: "flex-start",
							justifyContent: "flex-end",
							flexWrap: "nowrap"
						}}
						data-testid="profile-summary-actions"
					>
						{buttons &&
							buttons
								.filter((button) => {
									return button.viewable;
								})
								.map((button) => {
									const { name, toggled, disabled, action, icon, toggleColor } = button;
									return (
										<FontIconButton
											key={name}
											label={name}
											icon={icon}
											action={action}
											toggled={toggled}
											disabled={disabled}
											toggleColor={toggleColor}
										/>
									);
								})}
						{customActions &&
							customActions.map((action) => {
								const { label, type } = action;
								return (
									<FontIconButton
										key={label}
										label={label}
										icon={getIcon(type, 24)}
										action={getAction(action)}
									/>
								);
							})}
					</div>
				</div>
			)}
		</div>
	);
};

SummaryWidget.propTypes = propTypes;
SummaryWidget.defaultProps = defaultProps;

export default withSpan("summary-widget", "profile-widget")(SummaryWidget);
